import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import DOMPurify from 'dompurify';
import markdownIt from 'markdown-it';
import { SelectOption, notification, NotificationProps } from 'tea-component';
import { GSD_SOURCE_KEY } from '@intelligence/_config/sourceMetric';
import { ECHARTS_DATASOURCE_METRICS_MAP } from '@/pages/intelligence/utils/constant';
import {
  getDataSourcesDisabledStateParams,
  getDataSourcesDisabledStateResponse,
} from '@/pages/intelligence/utils/paramsInterface';
import {
  DateTypeFrontend,
  DateTypeBackend,
  DateTypeFrontendLow,
} from '@/pages/intelligence/utils/commonEnums';
import { getLocale } from 'umi';
import type { IRegion, IDateType, IDateTypely, IRegionCountry } from '../types/common';
import { getBasePath } from '@/utils/utils';
import { barType as mixedBarType } from '@/pages/intelligence/pages/GlobalView/components/NewDetailBar';
import {
  TopchartPageId,
  MSCIENCE_METRICS_REGIONS,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { isSafari } from './common';
import { decimalFormat } from '@/pages/home/utils/common';
import { isNullOrUndefined, normalizeCode } from '@intelligence/v3/utils/common';
import { getDateComparator } from '@intelligence/v3/utils/date';
import { SortType } from '@home/enums/common';
import type { AxiosResponseHeaders } from 'axios';
import type { ISelectOptionWithGroup } from '@intelligence/types/common';
import type { PCConsolePageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/type';

// 都保留两位小数
export function newMathFormat(data: any, showZero = false) {
  let unit = 'k';
  let value: any = Math.abs(data);
  if (showZero && !data) return 0;
  if (parseFloat(data) === 0) return 0; // 数据为0就要展示0， 无数据展示-
  if (!data || isNaN(data)) return '-';
  const isNegativeStr = data < 0 ? '-' : '';
  // 最外面的 parseFloat用于去除末尾无效的零
  if (value < 1000000 && value >= 1000) {
    unit = 'K';
    value = parseFloat((parseFloat(value) / 1000).toFixed(2));
  } else if (value >= 1000000 && value < 1000000000) {
    unit = 'M';
    value = parseFloat((parseFloat(value) / 1000000).toFixed(2));
  } else if (value >= 1000000000) {
    unit = 'B';
    value = parseFloat((parseFloat(value) / 1000000000).toFixed(2));
  } else if (value < 1000) {
    unit = '';
    value = parseFloat(parseFloat(value).toFixed(2));
  }
  // return isNegativeStr + Math.abs(value) + unit;

  // Math.abs会去掉末尾的0，为保证数据格式统一，所以不用
  if (value < 0) value = -value;
  return isNegativeStr + value + unit;
}

export function tableDataFormatKMB(data: number | string) {
  /** 0展示0 空展示- */
  if (
    data === null ||
    data === '' ||
    data === undefined ||
    isNaN(Number(data))
  ) {
    return '-';
  }
  // 此函数是保持2位小数的
  return newMathFormat(data);
}

// B，M，K保留2位小数, 坐标轴的话不显示小数,所以加paseInt处理
/**
 * data: 数值,
 * showZero：无数据时是否展示0，是展示0，否展示-,
 * min：小于1000的数值是否展示小数点,
 * hasAllPoin： 保留2位小数 如2.9, false展示2.9, true展示2.90 */
export function mathFormat(
  data: any,
  showZero = false,
  min = 1000,
  hasAllPoin = false,
) {
  let unit = 'k';
  let value: any = Math.abs(data);
  if (showZero && !data) return 0;
  if (parseFloat(data) === 0) return 0;
  if (!data || isNaN(data)) return '-';
  // 通过hasAllPoin判断如2.9是否展示2.90
  const toFixedOrparseFloat = (value, hasAllPoin) => {
    if (hasAllPoin) {
      return value;
    }
    return parseFloat(value);
  };
  const isNegativeStr = data < 0 ? '-' : '';
  // 最外面的 parseFloat用于去除末尾无效的零
  if (value < 1000000 && value >= 1000) {
    unit = 'K';
    value = toFixedOrparseFloat(
      (parseFloat(value) / 1000).toFixed(2),
      hasAllPoin,
    );
  } else if (value >= 1000000 && value < 1000000000) {
    unit = 'M';
    value = toFixedOrparseFloat(
      (parseFloat(value) / 1000000).toFixed(2),
      hasAllPoin,
    );
  } else if (value >= 1000000000) {
    unit = 'B';
    value = toFixedOrparseFloat(
      (parseFloat(value) / 1000000000).toFixed(2),
      hasAllPoin,
    );
  } else if (value < min) {
    unit = '';
    value = parseInt(data);
  } else {
    unit = '';
    value = toFixedOrparseFloat(parseFloat(value).toFixed(2), hasAllPoin);
  }
  // return isNegativeStr + Math.abs(value) + unit;

  // Math.abs会去掉末尾的0，为保证数据格式统一，所以不用
  if (value < 0) value = -value;
  return isNegativeStr + value + unit;
}
// 数据格式处理  -- 243K， 0展示0   空或-展示 -
export const valueFormat = (value: any, showZero = false, min = 1000) => {
  if (isNaN(value) || value === '' || value === null || value === '-') {
    return '-';
  }
  if (value === 0 || value === '0') {
    return 0;
  }
  return mathFormat(value, showZero, min);
};

const getValieData = (value: any, fixedNum = 2) => {
  if (isNaN(value) || value === '' || value === null || value === '-') {
    return '-';
  }
  if (value === 0 || value === '0') {
    return 0;
  }
  let n = fixedNum;
  while (value.toFixed(n) == 0) {
    n++;
  }
  return value.toFixed(n);
};

// 数据格式处理  -- 243K， 0展示0   空或-展示 -
export const valueFormatArpu = (value: any, showZero = false, min = 1000) => {
  if (isNaN(value) || value === '' || value === null || value === '-') {
    return '-';
  }
  if (value === 0 || value === '0') {
    return 0;
  }
  if (Math.abs(value) < 1) {
    return getValieData(value);
  }
  return mathFormat(value, showZero, min);
};

// 数据格式处理  -- 10%， 0展示0%   空或-展示 -
export const growthFormat = (params: {
  value: any;
  valueType:
  | 'pointValue'
  | 'percentValue'
  | string
  | 'rankValue' /** 后端返回是否是小数点还是已转为百分比数字 */;
  hasPoint?: 'none' | 'all' | 'lessThanOne' | any; // 所有的都有小数点，还是绝对值小于1的有，或者没有小数点
  fixedNum: number; // 小数点保留几位
  maxNumber: number;
  zeroValue?: string; // 如果为0时如何展示，默认展示-， 可以传值为 =， 0， 0%等自定义
}) => {
  const {
    value,
    valueType,
    hasPoint = 'lessThanOne',
    fixedNum = 2,
    maxNumber = 0,
    zeroValue,
  } = params;

  if (
    value === '' ||
    value === null ||
    value === '-' ||
    value === undefined ||
    Number.isNaN(value)
  ) {
    return { value: '-', type: '' };
  }
  let newVal: string | number = Math.abs(value); // newVal用于展示数值，没有正负符号
  let typeValue = value; // typeValue用地判断类型，有正负符号

  if (valueType === 'rankValue') {
    newVal = newVal;
  }
  if (valueType === 'pointValue') {
    newVal = newVal * 100;
    // typeValue用于判断类型，有正负符号
    typeValue = typeValue * 100;
  }
  if (valueType === 'percentValue') {
    newVal = newVal;
    // typeValue用于判断类型，有正负符号
    typeValue = typeValue;
  }

  if (hasPoint === 'none') {
    newVal = newVal.toFixed(0);
  } else if (hasPoint === 'all') {
    newVal = newVal.toFixed(fixedNum);
  } else if (hasPoint === 'lessThanOne') {
    newVal = newVal >= 1 ? newVal.toFixed(0) : newVal.toFixed(fixedNum);
  }

  if (valueType === 'rankValue') {
    if (Number(newVal) === 0) {
      return { value: zeroValue ?? '-', type: '' };
    }
    return {
      value: Number(newVal) > maxNumber ? `${maxNumber}+` : `${newVal}`,
      type: typeValue < 0 ? 'down' : typeValue > 0 ? 'up' : '',
    };
  }
  // 0.0025 为0
  if (Number(newVal) === 0) {
    return { value: zeroValue ?? '0%', type: '' };
  }
  return {
    value: Number(newVal) > maxNumber ? `${maxNumber}+%` : `${newVal}%`,
    type: typeValue < 0 ? 'down' : typeValue > 0 ? 'up' : '',
  };
};

export const handleDataFormat = (value: any, segVal: any) => {
  if (value === '' || value === null || value === '-' || value === undefined) {
    return '-';
  }
  if (value === 0 || value === '0') {
    return 0;
  }
  if (['arpu', 'arppu'].includes(segVal)) {
    return Number(value)?.toFixed(2);
  }
  if (['d2', 'pay_permeability'].includes(segVal)) {
    return `${Number(value)?.toFixed(2)}%`;
  }
  return valueFormat(value);
};

/**
 * @description: 格式化数字
 * @param {Number||String} num
 * @param {boolean} isToFixed 是否保留两位小数
 * @param {number} fixedNumber 保留几位小数
 * @return {String}
 */
export function formatNum(num: any, isToFixed = false, fixedNumber = 2) {
  const s = new String(num);
  const arr = s.split('.');
  if (arr[0]?.length > 3) {
    // 大于一千
    // 将数字转换为字符串，进行三位分割
    const _num = `${arr[0]}`.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
    // 整数
    const x = new Number(_num.split(',')[0]);
    // 小数
    const x2 = _num.split(',')[1];
    const _arr = ['K', 'M', 'B', 'T'];
    if (!isToFixed) {
      return `${Math.round(new Number(`${x}.${x2}`) * 100) / 100}${_arr[_num.split(',')?.length - 2]
        }`;
    }
    return `${(Math.round(new Number(`${x}.${x2}`) * 100) / 100).toFixed(
      fixedNumber,
    )}${_arr[_num.split(',')?.length - 2]}`;
  }
  if (arr[1]) {
    // 小于一千且有小数
    if (!isToFixed) {
      return Math.round(new Number(`${arr[0]}.${arr[1]}`) * 100) / 100;
    }
    return (Math.round(new Number(`${arr[0]}.${arr[1]}`) * 100) / 100).toFixed(
      fixedNumber,
    );
  } // 小于一千没有小数
  return num;
}

export function formatCalendar(bDate: any, eDate: any, calendarType = 'Day') {
  const monthDic = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sep',
    10: 'Oct',
    11: 'Nov',
    12: 'Dec',
  } as any;
  if (bDate === eDate) {
    eDate = '';
  }
  let bString;
  let year;
  let bMonth;
  let eString;
  let bDay;
  let eDateArr;
  let eMonth;
  let eDay;
  if (bDate && bDate != '') {
    const bDateArr = bDate.split('-');
    year = `, ${bDateArr[0]}`;
    bMonth = monthDic[bDateArr[1]];
    bDay = String(parseInt(bDateArr[2]));
    bString = `${bMonth}${calendarType === 'Day' || calendarType === 'Week' ? ` ${bDay}` : ''
      }`;
  } else {
    bString = '';
    year = '';
  }
  if (eDate && eDate != '') {
    eDateArr = eDate.split('-');
    eMonth = monthDic[eDateArr[1]];
    eDay = String(parseInt(eDateArr[2]));
    eString = ` - ${eMonth}${calendarType === 'Day' || calendarType === 'Week' ? ` ${eDay}` : ''
      }`;
    year = `, ${eDateArr[0]}`;
  } else {
    eString = '';
  }
  return `${bString}${eString}${year}`;
}

/**
 * 判断选择时间范围是否包含下架时间
 */
export function hanPullOffTime(dateValue: any, lowerGameTime: any) {
  const dateS = moment(dateValue[0]).valueOf();
  const dateE = moment(dateValue[1]).valueOf();
  const OffS = moment(lowerGameTime[0]).valueOf();
  const OffE = moment(lowerGameTime[1]).valueOf();

  if (dateValue.length > 1 && lowerGameTime.length > 1) {
    if (dateE < OffS || dateS > OffE) {
      return false;
    }
    return true;
  }
  return false;
}

// 字母排序规则
function letterSort(
  a: any,
  b: any,
  key = '',
  currentSortIndex: -1 | 1,
): number {
  const A = key ? a?.[key]?.toUpperCase() : a?.toUpperCase();
  const B = key ? b?.[key]?.toUpperCase() : b?.toUpperCase();
  if (A < B) {
    return -currentSortIndex;
  }
  if (A > B) {
    return currentSortIndex;
  }
  return 0;
}
// 数字排序规则
function numberSort(
  a: any,
  b: any,
  key = '',
  currentSortIndex: -1 | 1,
  suspenseSort = false,
  suspensePattern: 'string' | 'number' = 'number',
  suspenseKey = '',
): number {
  const aVal = a?.[key] || a;
  const bVal = b?.[key] || b;
  const aNum = Number.parseFloat(aVal);
  const bNum = Number.parseFloat(bVal);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    if (aNum < bNum) {
      return -currentSortIndex;
    }
    if (aNum > bNum) {
      return currentSortIndex;
    }
    return 0;
  }
  // 都是无效数据及都为0时的排序规则
  if (
    (Number.isNaN(aNum) && Number.isNaN(bNum)) ||
    (aNum === 0 && bNum === 0)
  ) {
    if (!suspenseSort) return 0;
    // 如果是数字排序
    if (suspensePattern === 'number') {
      return numberSort(a, b, suspenseKey, currentSortIndex, false);
    }

    // 如果是字符串排序
    return letterSort(a, b, suspenseKey, currentSortIndex);
  }
  return Number.isNaN(aNum) ? -currentSortIndex : currentSortIndex;
}
/**
 * 将数组按某个字段排序
 * @param originalArr 原数组
 * @param pattern 字符串排序还是数字排序
 * @param sortType 排序规则: 升序asc排序（或A-Z排序）/降序desc排序（或Z-A排序）
 * @param key 遵循哪个字段排序
 * @param suspenseSort 两个都是无效数据时是否按其它字段排序（注：数字排序时，两个都为0时也准从这个备用排序）
 * @param suspensePattern 两个都是无效数据时的备用排序模式
 * @param suspenseKey 两个都是无效数据时的备用排序字段
 * @returns 排序后的数组
 */
export const getArraySort = (
  originalArr: any[],
  pattern: 'string' | 'number' = 'number',
  sortType: 'asc' | 'desc' = 'asc',
  key = '',
  suspenseSort = false,
  suspensePattern: 'string' | 'number' = 'number',
  suspenseKey = '',
): any[] => {
  if (!Array.isArray(originalArr)) return originalArr;
  const sortArr = originalArr?.sort((a, b) => {
    // 区分asc或desc排序
    const currentSortIndex = sortType === 'desc' ? -1 : 1;

    // 如果是数字排序
    if (pattern === 'number') {
      return numberSort(
        a,
        b,
        key,
        currentSortIndex,
        suspenseSort,
        suspensePattern,
        suspenseKey,
      );
    }

    // 如果是字符串排序
    return letterSort(a, b, key, currentSortIndex);
  });

  return sortArr;
};
/**
 * 根据国家编码 获取国家名称
 * @param regionsCountries 国家筛选器选项数据 全量国家的
 * @param countryCode 国家编码
 * @returns 国家名称
 */
const PASSTHROUGH_COUNTRY_MARKETS = new Set([
  'global',
  'global_wo_china',
  'total',
  'others',
  'unselected',
]);

/**
 * 合并多份地区国家数据（如 common + rpd），供国家 code / 名称解析使用
 */
export function mergeRegionsCountriesList(
  sources: (IRegion[] | null | undefined)[],
): IRegion[] {
  const countryByCode = new Map<string, IRegionCountry>();
  let baseRegions: IRegion[] = [];

  sources.forEach(source => {
    if (!Array.isArray(source) || source.length === 0) return;
    if (!baseRegions.length) baseRegions = source;
    source.forEach(region => {
      (region?.country_set || []).forEach(country => {
        const code = country?.country_code?.toLowerCase();
        if (code) countryByCode.set(code, country);
      });
    });
  });

  if (!countryByCode.size) return baseRegions;

  return [
    ...baseRegions,
    {
      area_code: '__merged__',
      area_name_en: '',
      area_name_ch: '',
      country_set: [...countryByCode.values()],
    },
  ];
}

/**
 * 将 market 字段（国家 code 或英文/中文名称）解析为标准国家 code
 */
export function resolveCountryMarketCode(
  market: string,
  regionsCountries?: IRegion[] | null,
): string {
  if (typeof market !== 'string' || !market) return market || '';
  const trimmedMarket = market.trim();
  const lower = trimmedMarket.toLowerCase();
  if (PASSTHROUGH_COUNTRY_MARKETS.has(lower)) return lower;
  if (trimmedMarket === 'Others') return 'others';

  const countryByCode = getCountryByCode(lower, regionsCountries);
  if (countryByCode) {
    return countryByCode.country_code?.toLowerCase() || lower;
  }

  const mapped = getCountryCodeByCountryName(regionsCountries || [], trimmedMarket);
  if (mapped && mapped.toLowerCase() !== lower) {
    return mapped.toLowerCase();
  }

  const shortName = trimmedMarket.split(',')[0]?.trim();
  if (shortName && shortName !== trimmedMarket) {
    const mappedShort = getCountryCodeByCountryName(
      regionsCountries || [],
      shortName,
    );
    if (mappedShort && mappedShort.toLowerCase() !== shortName.toLowerCase()) {
      return mappedShort.toLowerCase();
    }
  }

  return lower;
}

/**
 * 将国家表格行中的 market 统一为国家 code，并补全 name / name_cn
 */
export function normalizeCountryMarketRow(
  row: any,
  regionsCountries?: IRegion[] | null,
): any {
  const rawMarket = row?.market;
  if (typeof rawMarket !== 'string') return row;

  const marketCode = resolveCountryMarketCode(rawMarket, regionsCountries);
  const country = getCountryByCode(marketCode, regionsCountries);

  return {
    ...row,
    market: marketCode,
    name: country?.country_name_en || row?.name || rawMarket,
    name_cn: country?.country_name_ch || row?.name_cn || row?.name || rawMarket,
  };
}

export function normalizeCountryMarketRows(
  list: any[],
  regionsCountries?: IRegion[] | null,
): any[] {
  if (!Array.isArray(list) || list.length === 0) return list || [];
  return list.map(row => normalizeCountryMarketRow(row, regionsCountries));
}

export function getCountryNameByCountryCode(
  regionsCountries: IRegion[] | undefined | null,
  countryCode: string,
  regionType = 'market',
): string {
  const locale = getLocale();
  const isCn = locale === 'zh-CN';
  if (typeof countryCode !== 'string') return '';
  const normalizedCode = countryCode.toLowerCase();
  if (normalizedCode === 'global') return isCn ? '全球' : 'Global';
  if (normalizedCode === 'unselected') return 'Unselected';
  if (normalizedCode === 'others') return isCn ? '其他' : 'Others';
  if (!Array.isArray(regionsCountries)) return countryCode;
  if (regionType === 'region') {
    for (const regionCountries of regionsCountries) {
      if (
        normalizedCode === regionCountries?.area_code?.toLowerCase() ||
        normalizedCode === regionCountries?.area_name_en?.toLowerCase()
      ) {
        return (
          (isCn
            ? regionCountries?.area_name_ch
            : regionCountries?.area_name_en) || countryCode
        );
      }
    }
    return countryCode;
  }

  const country = getCountryByCode(
    resolveCountryMarketCode(countryCode, regionsCountries),
    regionsCountries,
  );
  if (country) {
    return (
      (isCn ? country.country_name_ch : country.country_name_en) || countryCode
    );
  }
  return countryCode;
}

/**
 * 根据地区编码获取地区名称
 * @param regionsCountries 国家筛选器选项数据 全量地区国家的
 * @param regionCode 地区编码
 * @returns 地区名称
 */
export const getRegionNameByRegionCodeOrName = (regionsCountries: IRegion[], regionCode: string): string => {
  if (typeof regionCode !== 'string') {
    return '';
  }

  if (!Array.isArray(regionsCountries) || regionsCountries.length === 0) {
    return regionCode;
  }

  const locale = getLocale();
  const isCn = locale === 'zh-CN';
  const normalizedRegionCode = normalizeCode(regionCode);

  for (const regionCountries of regionsCountries) {
    const normalizedAreaCode = normalizeCode(regionCountries?.area_code);
    const normalizedAreaNameEn = normalizeCode(regionCountries?.area_name_en);
    if (normalizedRegionCode === normalizedAreaCode || normalizedRegionCode === normalizedAreaNameEn) {
      return (isCn ? regionCountries?.area_name_ch : regionCountries?.area_name_en) || regionCode;
    }
  }

  return regionCode;
};

/**
 * 根据国家全称 获取国家编码
 * @param regionsCountries 国家筛选器选项数据 全量国家的
 * @param countryName 国家全称
 * @param ignoreLocale 是否忽略语言
 * @returns 国家编码
 */
export function getCountryCodeByCountryName(
  regionsCountries: IRegion[],
  countryName: string,
  ignoreLocale?: boolean,
): string {
  const locale = getLocale();
  const isCn = locale === 'zh-CN';
  if (typeof countryName !== 'string') return '';
  const normalizedName = countryName.trim();
  if (!Array.isArray(regionsCountries)) return normalizedName;
  for (const regionCountries of regionsCountries) {
    const countries = regionCountries?.country_set;
    if (!Array.isArray(countries)) continue;
    for (const country of countries) {
      const enComparison =
        normalizedName.toLocaleLowerCase() ===
        country?.country_name_en?.trim()?.toLocaleLowerCase();
      const cnComparison = normalizedName === country?.country_name_ch?.trim();
      const isFind = ignoreLocale
        ? isCn
          ? cnComparison
          : enComparison
        : enComparison || cnComparison;
      if (isFind) {
        return country?.country_code || normalizedName;
      }
    }
  }
  return normalizedName;
}

/**
 * 根据国家编码 获取国家名称
 * @param regionsCountries 国家列表数据
 * @param countryCode 国家编码
 * @returns 国家名称
 */
export function getCountryNameByCountryCodeSimple(
  countries: any[],
  countryCode: string,
): string {
  if (typeof countryCode !== 'string') return '';
  if (!Array.isArray(countries)) return countryCode;
  let countryName = '';
  for (const country of countries) {
    if (country?.abbre === countryCode) {
      countryName = country?.name;
      break;
    }
  }
  return countryName || countryCode;
}

// 平均分割一个数组，并将分割后的数组组合成一个新的数组
export const getNewAverageArray = (arr: any, size: any) => {
  // size=5，要分割的长度
  const arrNum = Math.ceil(arr?.length / size, 10); // Math.ceil()向上取整的方法，用来计算拆分后数组的长度
  let index = 0; // 定义初始索引
  let resIndex = 0; // 用来保存每次拆分的长度
  const result = [];
  while (index < arrNum) {
    result[index] = arr?.slice(resIndex, size + resIndex);
    resIndex += size;
    index++;
  }
  return result;
};

// 分页器
export const pageAble = (list: any[], pageindex: number, pagesize: number) => {
  if (!list) {
    return [];
  }
  const _list = list?.slice(0);
  const pageAbleList = _list.slice(
    (pageindex - 1) * pagesize,
    pageindex * pagesize,
  );
  return pageAbleList;
};

// 校验value是否为undefined、nulll、空字符串（trim操作后），空数组、空对象
export function isEmpty(v: any): boolean {
  return (
    v === void 0 ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (typeof v === 'string' && v.trim() === '-') ||
    (Array.isArray(v) && v.length === 0) ||
    (Object.prototype.toString.call(v) === '[object Object]' &&
      Object.keys(v).length === 0)
  );
}

// 判断是否为对象
export const isObject = (v: any): boolean =>
  Object.prototype.toString.call(v) === '[object Object]';

// 判断是否为对象为空
export const isObjectEmpty = (v: any): boolean => Object.keys(v).length === 0;

/**
 * 指定字符串 转换为首字符大写
 * @param str 原字符串
 * @param firstEndLowerCase 从第二个字符开始剩余的字符，是否转换为小写
 * @param allTranslation 是否全部单词转换
 * @returns 转换后的字符串
 */
export function toFirstUpperCase(
  str: string,
  firstEndLowerCase = false,
  allTranslation = true,
): string {
  if (typeof str !== 'string') return '';
  if (allTranslation) {
    try {
      return str
        ?.split(' ')
        ?.map(
          (t: string) =>
            t.substring(0, 1).toUpperCase() +
            (firstEndLowerCase ? t.substring(1).toLowerCase() : t.substring(1)),
        )
        ?.join(' ');
    } catch (error) {
      return str;
    }
  } else {
    const firstLetter = str.substring(0, 1).toUpperCase();
    const remainsLetters = firstEndLowerCase
      ? str.substring(1).toLowerCase()
      : str.substring(1);
    return firstLetter + remainsLetters;
  }
}

// 只有global和globalexeptchina是region,其他发market
export const trankMarketsParamsExceptGlobal = (params: {
  regionType?: string;
  regions: string[];
  markets: string[];
}) => {
  const { regionType = 'market', regions, markets } = params;
  const marketsExcptGlobal = {
    regionType,
    regions,
  };
  if (
    Array.isArray(regions) &&
    (regions[0] === 'global' || regions[0] === 'global_wo_china')
  ) {
    marketsExcptGlobal.regionType = 'region';
    marketsExcptGlobal.regions = regions;
  } else {
    marketsExcptGlobal.regionType = 'market';
    marketsExcptGlobal.regions = markets;
  }
  return marketsExcptGlobal;
};

/**
 * 判断是否为正数
 * @param val 传进来的值
 * @returns 判断结果
 */
export const isPositiveNum = (val: any) => {
  if (isNaN(Number(val))) return false;
  // 判断是否是正数
  const reg = /^\d+(?=\.{0,1}\d+$|$)/;
  return reg.test(val);
};

// 将内容转换成有效的数字
export function vaildNum(value) {
  if (value === '.' || !value) return '0';

  const beforeZero = value?.slice(0, 1) === '0' && value?.slice(1, 2) !== '.'; // 首位输入的是 0且后面没有小数点

  value = value.replace(/[^\d.]/g, ''); // 清除“数字”和“.”以外的字符

  value = value.replace(/\.{2,}/g, '.'); // 只保留第一个. 清除多余的

  value = value.replace('.', '$#$').replace(/\./g, '').replace('$#$', '.');

  // value = value.replace(/^(\-)*(\d+)\.(\d\d).*$/,'$1$2.$3'); //只能输入两位小数

  const res = parseFloat(value)?.toString() === 'NaN' ? '0' : value;

  return beforeZero ? (res?.slice(1) ? res?.slice(1) : '0') : res;
}

/**
 * 获取指定文本内容的宽度
 * @param text 文本内容
 * @param option.fontSize 字体大小
 * @param option.fontWeight 字体粗细
 * @returns 文本内容的宽度
 */
export function getActualWidthOfChars(
  text: string,
  option?: {
    fontSize?: number;
    fontWeight?: number;
  },
): number {
  if (typeof text !== 'string') return 0;
  // const { fontSize = 12, fontWeight = 400 } = option || {};
  // const node = document.createElement('span');
  // node.innerText = text;
  // node.style.fontSize = `${fontSize}px`;
  // node.style.fontWeight = `${fontWeight}`;
  // node.style.display = 'none';
  // document.body.append(node);
  // const widthStr = getComputedStyle(node, null).getPropertyValue('width');
  // const width = Number.parseFloat(widthStr);
  // return Number.isNaN(width) ? 0 : width;

  const fontFamily = `"Roboto", "system-ui", "Microsoft YaHei", Helvetica, Arial, "Hiragino Sans GB", "Heiti SC", "Helvetica Neue", "WenQuanYi Micro Hei", sans-serif`;
  const { fontSize = 12, fontWeight = 400 } = option || {};
  const canvas = document.createElement('canvas');
  const ctx: any = canvas.getContext('2d');
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const metrics = ctx.measureText(text);
  const actual =
    Math.abs(metrics.actualBoundingBoxLeft) +
    Math.abs(metrics.actualBoundingBoxRight);
  return Math.max(metrics.width, actual);
}

export const showTimeByDateType = (value: string, dateType: string) => {
  if (!moment(value).isValid()) {
    return '';
  }
  if (
    typeof dateType === 'string' &&
    dateType?.toLocaleLowerCase().indexOf('month') > -1
  ) {
    return moment(value).format('YYYY-MM');
  }
  if (
    typeof dateType === 'string' &&
    ['hourly', 'hour'].includes(dateType.toLowerCase())
  ) {
    return moment(value).format('YYYY-MM-DD HH:mm');
  }
  return moment(value).format('YYYY-MM-DD');
};

export const getDataSourcesMetricsByTopChartsType = (
  topChartsType: string,
): any[] => {
  return ECHARTS_DATASOURCE_METRICS_MAP[topChartsType] || [];
};

/**
 * 根据regions、markets 判断 不同数据源禁用状态
 * @param params 请求参数，包含regions、markets即可
 * @returns 数据源禁用状态，包含 steam、Mscience、npd、newzoo（包含加权的指标）
 */
export const getDataSourcesDisabledState = (
  params: getDataSourcesDisabledStateParams,
): getDataSourcesDisabledStateResponse => {
  const regions =
    params?.regions?.map((item: string) => item?.toLowerCase()) || [];
  const markets =
    params?.markets?.map((item: string) => item?.toLowerCase()) || [];
  // 特殊情况，physicalRevenue是仅美国，但仍然需要stream下载
  const isSteamCan = !!(
    params?.currentMetric && params?.currentMetric === 'physicalRevenue'
  );
  // 国家筛选器非global时，steam不适用，置灰。
  const isGlobal = regions?.includes('global');
  // 国家筛选器未选美国时，NPD不适用，置灰。
  const isOnlyUS =
    isGlobal || regions?.includes('us') || markets?.includes('us');
  // 国家筛选器未选美英法德西时（已选国家不包含），Mscience不适用，置灰。
  const isIncludeMYFDX =
    isGlobal ||
    regions?.some((item: string) => MSCIENCE_METRICS_REGIONS.includes(item)) ||
    markets?.some((item: string) => MSCIENCE_METRICS_REGIONS.includes(item));
  // 时段筛选器选择Daily/Monthly时，npd、Newzoo不适用，置灰。
  const isDayOrWeek = [
    DateTypeFrontend.Day,
    DateTypeBackend.Day,
    DateTypeFrontendLow.Day,
    DateTypeFrontend.Week,
    DateTypeBackend.Week,
    DateTypeFrontendLow.Week,
  ].includes(params?.date_type || params?.period);
  const isWeek = [
    DateTypeFrontend.Week,
    DateTypeBackend.Week,
    DateTypeFrontendLow.Week,
  ].includes(params?.date_type || params?.period);
  return {
    steam: isSteamCan ? false : !isGlobal,
    Mscience: !isIncludeMYFDX,
    npd: !isOnlyUS || isDayOrWeek,
    newzoo: isDayOrWeek,
    stream: isSteamCan ? false : !isGlobal,
    famitsu: !isWeek,
  };
};

/**
 * 根据regions、markets 判断 不同数据源禁用状态(兼容pc、console、pcConsole)
 * @param params 请求参数，包含regions、markets即可
 * @returns 数据源禁用状态，包含 steam、Mscience、npd、newzoo（包含加权的指标）
 */
export const getDataSourcesDisabledStateNew = (cfg: {
  pageId: PCConsolePageId;
  params: getDataSourcesDisabledStateParams;
}): getDataSourcesDisabledStateResponse => {
  const pageId = cfg?.pageId || TopchartPageId.MetricsTable;
  const params = cfg?.params || {};
  const regions =
    params?.regions?.map((item: string) => item?.toLowerCase()) || [];
  const markets =
    params?.markets?.map((item: string) => item?.toLowerCase()) || [];
  // 特殊情况，physicalRevenue是仅美国，但仍然需要stream下载
  const isSteamCan = params?.currentMetric === 'physicalRevenue';
  const isVginsightsCan = params?.currentMetric === 'physicalRevenue';

  // 国家筛选器非global时，steam不适用，置灰。
  const isGlobal = regions?.includes('global') || markets?.includes('global');
  // 国家筛选器未选美国时，NPD不适用，置灰。
  const isOnlyUS =
    isGlobal || regions?.includes('us') || markets?.includes('us');
  const isOnlyJapan =
    isGlobal || regions?.includes('jp') || markets?.includes('jp');
  // 国家筛选器未选美英法德西时（已选国家不包含），Mscience不适用，置灰。
  const isIncludeMYFDX =
    isGlobal ||
    regions?.some((item: string) => MSCIENCE_METRICS_REGIONS.includes(item)) ||
    markets?.some((item: string) => MSCIENCE_METRICS_REGIONS.includes(item));
  const isMonth = [
    DateTypeFrontend.Month,
    DateTypeBackend.Month,
    DateTypeFrontendLow.Month,
  ].includes(params?.date_type || params?.period);
  const isWeek = [
    DateTypeFrontend.Week,
    DateTypeBackend.Week,
    DateTypeFrontendLow.Week,
  ].includes(params?.date_type || params?.period);

  let dataSourcesDisabledState: any = {
    steam: isSteamCan ? false : !isGlobal,
    // 只有明确返回 false 才禁用 Databrain；undefined 表示旧页面未提供校准状态，保持原行为。
    databrain: params?.hasDatabrainData === false,
    Mscience: !isIncludeMYFDX,
    [GSD_SOURCE_KEY]: !isWeek,
    mscience: !isIncludeMYFDX,
    npd: !(isOnlyUS && isMonth),
    newzoo: !isMonth,
    stream: isSteamCan ? false : !isGlobal,
    famitsu: !isWeek,
    vginsights: isVginsightsCan ? false : !isGlobal,
    gamalytic: !isGlobal,
    alinea: !isGlobal,
    ampere: isWeek,
    metacritic: !(
      isGlobal &&
      params?.date_type === 'daily' &&
      params?.start_date === params?.end_date
    ),
  };
  if (pageId === TopchartPageId.PCConsoleMetricsTable) {
    // const devices: string[] = getAggregatedDevicesValues(params?.device);
    dataSourcesDisabledState = {
      ...dataSourcesDisabledState,
      // steamData 需要有 PC 设备，pcConsoleMetricData 接口中 PC 数据字段名为 Steam
      vginsights: !isGlobal,
      gamalytic: !isGlobal,
      alinea: !isGlobal,
      // vginsights: !(isGlobal && devices?.some((device) => ['PC', 'Steam'].includes(device))),
      famitsu: !(isOnlyJapan && isWeek),
      // famitsu: !(
      //   isOnlyJapan &&
      //   isWeek &&
      //   devices?.some((device) => CONSOLE_AGGREGATED_DEVICES.includes(device))
      // ),
    };
  }
  return dataSourcesDisabledState;
};

/**
 * 获取指定指标是否禁用
 * @param metric 指标
 * @param isDataSourceDisabled 当前指标所属数据源 是否禁用
 * @param params 查询参数对象，包含 regions、markets即可
 * @returns 改指标是否禁用
 */
export const getMetricDisabled = (
  metric: string,
  isDataSourceDisabled: boolean,
  params: getDataSourcesDisabledStateParams,
): boolean => {
  if (isDataSourceDisabled) return isDataSourceDisabled;
  // digitalARPPU指标 仅选美国时可用
  if (metric === 'digitalARPPU') {
    const regions =
      params?.regions?.map((item: string) => item?.toLowerCase()) || [];
    const markets =
      params?.markets?.map((item: string) => item?.toLowerCase()) || [];
    const isOnlyUS =
      (regions?.length === 1 && regions[0] === 'us') ||
      (markets?.length === 1 && markets[0] === 'us');
    return !isOnlyUS;
  }
  return false;
};

/**
 * 获取指定指标是否禁用（兼容pc、console、pcConsole）
 * @param metric 指标
 * @param isDataSourceDisabled 当前指标所属数据源 是否禁用
 * @param params 查询参数对象，包含 regions、markets即可
 * @returns 改指标是否禁用
 */
export const getMetricDisabledNew = (cfg: {
  pageId: PCConsolePageId;
  metric: string;
  isDataSourceDisabled: boolean;
  params: getDataSourcesDisabledStateParams;
}): boolean => {
  const pageId = cfg?.pageId || TopchartPageId.MetricsTable;
  const metric = cfg?.metric || '';
  const isDataSourceDisabled = cfg?.isDataSourceDisabled || false;
  const params = cfg?.params || {};
  if (metric === 'digitalARPPU') {
    /**
     * digitalARPPU指标 仅选Global或者包含美国时可用（pcConsole统一id MetricsTable榜单页）
     */
    const regions =
      params?.regions?.map((item: string) => item?.toLowerCase()) || [];
    const markets =
      params?.markets?.map((item: string) => item?.toLowerCase()) || [];

    const isGlobal = regions?.includes('global');
    const isOnlyUS = regions?.includes('us') || markets?.includes('us');
    if (
      pageId === TopchartPageId.MetricsTable ||
      pageId === TopchartPageId.PCConsoleMetricsTable
    )
      return !(isGlobal || isOnlyUS);
  }
  if (isDataSourceDisabled) return isDataSourceDisabled;
  if (metric !== 'digitalARPPU') return false;
  /**
   * digitalARPPU指标 仅选美国时可用（pc端MetricsTable榜单页、console端MetricsTable榜单页）
   * digitalARPPU指标 仅选Global或者美国时可用（pcConsole统一id MetricsTable榜单页）
   */
  const regions =
    params?.regions?.map((item: string) => item?.toLowerCase()) || [];
  const markets =
    params?.markets?.map((item: string) => item?.toLowerCase()) || [];
  // 国家筛选器非global时，steam不适用，置灰。
  const isGlobal = regions?.includes('global');
  const isOnlyUS =
    (regions?.length === 1 && regions[0] === 'us') ||
    (markets?.length === 1 && markets[0] === 'us');
  // if (pageId === TopchartPageId.MetricsTable) return !isOnlyUS;
  // if (pageId === TopchartPageId.PCConsoleMetricsTable) return !(isGlobal || isOnlyUS);
  if (
    pageId === TopchartPageId.MetricsTable ||
    pageId === TopchartPageId.PCConsoleMetricsTable
  )
    return !(isGlobal || isOnlyUS);
  return false;
};

/**
 * @description: 格式化百分比，超出99999%限制输出为99999%+
 * @param {Number||String} num
 * @return {String}
 */
export const formatPercentNum = (num: string | number) => {
  if (!num) return '0';
  if (typeof num !== 'number') return num;
  const _num = Number(num);
  let _percentage = 0;
  if (Math.abs(_num) > 0 && Math.abs(_num) < 0.01) {
    // 四舍五入保留两位小数
    _percentage = Number((Math.round(Math.abs(_num) * 10000) / 100).toFixed(2));
  } else {
    _percentage = Math.abs(Math.round(_num * 100));
  }
  return _percentage > 99999 ? '99999%+' : `${_percentage}%`;
};

/**
 * 四舍五入，避免精度丢失问题
 *
 * @export
 * @param {number} num
 * @param {number} s
 * @returns {number}
 */
export function toFixed(num: number, s: number) {
  const times = Math.pow(10, s);
  const des = `${num * times + 0.5}`;
  const result = parseInt(des, 10) / times;
  return result;
}

// 给跳转的url添加上basePath
export const addV2Prefix = (path?: string) => {
  if (!path) {
    return '';
  }
  if (path?.startsWith('http')) {
    return path;
  }
  const basePath = getBasePath();
  // 如果开头没有斜杠，则添加斜杠
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  // 如果路径已经包含basePath，则直接返回原路径
  if (trimmedPath.startsWith(`${basePath}/`)) {
    return trimmedPath;
  }
  // 添加 basePath 前缀
  return basePath + trimmedPath;
};

/**
 * 根据指定width、height 压缩cdn图片图片
 * @param image 图片url
 * @param width 压缩后的width
 * @param height 压缩后的height
 * @returns 压缩后的图片url
 */
export const addImageMogr2Url = (
  image: string,
  width: number,
  height = width,
): string => {
  if (!image) return '';
  return `${image}?imageMogr2/thumbnail/${width}x${height}`;
};

/**
 * 获取有效的当前tab页签（根据路由获取tab页签时，若路由上的tab页签 不存在，则重定向tab为指定默认激活tab 或者 tabs的第一个tab）
 * @param activeId 当前激活的tab页签（待校验是否有效）
 * @param cfg.tabs tabs页签
 * @param cfg.defaultActiveId 默认激活的tab页签
 * @returns 有效的激活的页签
 */
export const getValidActiveId = (
  activeId: string,
  cfg: {
    tabs: any[];
    defaultActiveId: string;
  },
): string => {
  const { tabs, defaultActiveId } = cfg ?? {};
  if (!Array.isArray(tabs) || !tabs.length) return defaultActiveId ?? '';
  const activeIds = tabs?.map(tab => tab?.id);
  return activeIds?.includes(activeId)
    ? activeId
    : defaultActiveId ?? tabs?.[0]?.id;
};

export const getValidOptionValue = (
  value: string,
  options: { value: string;[key: string]: any }[],
): string => {
  const option = options?.find(item => item?.value === value);
  return option?.value || options?.[0]?.value || '';
};

export const getSortedDates = (
  dates: string[],
  order: 'asc' | 'desc',
): string[] => {
  if (!Array.isArray(dates)) return [];
  // 创建新数组，避免排序影响到原数组
  const result = [...dates];
  result.sort((a, b) => {
    if (a === b) return 0;
    const aM = moment(a);
    const bM = moment(b);
    const num = aM.isAfter(bM) ? 1 : -1;
    return order === 'asc' ? num : -num;
  });
  return result;
};

const DATE_TYPES: IDateType[] = ['date', 'week', 'month'];
export const getValidDateType = (
  dateType: IDateType,
  dateTypes: IDateType[],
): IDateType => {
  const dateTypesTmp =
    Array.isArray(dateTypes) && dateTypes.length > 0 ? dateTypes : ['date'];
  return DATE_TYPES.includes(dateType) && dateTypesTmp.includes(dateType)
    ? dateType
    : DATE_TYPES[0];
};

const GRANULARYTIES: IDateTypely[] = ['daily', 'weekly', 'monthly'];
export const getValidGranularity = (
  granularity: IDateTypely,
  granularities: IDateTypely[],
): IDateTypely => {
  const granularitiesTmp =
    Array.isArray(granularities) && granularities.length > 0
      ? granularities
      : ['daily'];
  return GRANULARYTIES.includes(granularity) &&
    granularitiesTmp.includes(granularity)
    ? granularity
    : GRANULARYTIES[0];
};

type GetTextWithHighlightOption = {
  // 是否拆分成单个字符 判断是否需要高亮
  isSeparate?: boolean;
  // 是否忽略大小写 判断是否高亮
  isIgnoreCase?: boolean;
  // 匹配的关键字dom节点的class类名
  className?: string;
};

// ！！！切记不用动这个东西
let HIGHLIGHT_NODE_UNIQUE_COUNT = 1;

const getMatchedKeywordPositions = (
  text?: string,
  keyword?: string,
): [number, number][] => {
  if (!text || !keyword) return [];
  const positions: [number, number][] = [];
  const keywordLength = keyword.length;
  let currentIndex = -1;
  let startIndex = 0;
  while ((currentIndex = text.indexOf(keyword, startIndex)) !== -1) {
    startIndex = currentIndex + keywordLength;
    positions.push([currentIndex, startIndex - 1]);
  }
  return positions;
};

const replaceMatchedKeywordWithReactElement = (
  isIgnoreCase: boolean,
  str: string,
  keyword: string,
  reactElementGenerator: (payload: string) => React.ReactElement,
): string | (string | React.ReactElement)[] => {
  if (!str) return '';
  if (!keyword) return str;
  // 创建一个数组，用于存放最终的 JSX 元素
  const elements: (string | React.ReactElement)[] = [];
  const searchStr = !isIgnoreCase ? str : str?.toLowerCase();
  const searchKeyword = !isIgnoreCase ? keyword : keyword?.toLowerCase();
  const positions: [number, number][] = getMatchedKeywordPositions(
    searchStr,
    searchKeyword,
  );
  let currentIndex = 0;
  if (!Array.isArray(positions) || !positions.length) return str;

  for (const position of positions) {
    const [start, end] = position || [];
    const prevStr = str.substring(currentIndex, start);
    const matchedStr = str.substring(start, end + 1);
    if (!!prevStr) {
      elements.push(prevStr);
    }
    if (!!matchedStr) {
      elements.push(reactElementGenerator(matchedStr));
    }
    currentIndex = end + 1;
  }

  if (currentIndex < str.length) {
    elements.push(str.substring(currentIndex));
  }

  // 返回包含所有 JSX 元素的数组（或可以使用 React.Fragment 将其包裹后返回）
  return elements;
};

/**
 * 获取指定文本，指定关键字 高亮处理指定文本后的 ReactNode[]
 * @param text 指定文本
 * @param keyword 关键字
 * @param options 配置项，参考GetTextWithHighlightOption的注释，默认 拆分、忽略大小写
 * @returns 返回高亮处理后的ReactNode[]
 */
export const getTextWithHighlight = (
  text: string,
  keyword: string,
  options?: GetTextWithHighlightOption,
) => {
  if (typeof text !== 'string' || typeof keyword !== 'string') return text;

  const {
    isSeparate = true,
    isIgnoreCase = true,
    className = '',
  } = options || {};
  const keywordTemp = isIgnoreCase ? keyword.toLowerCase() : keyword;
  const matchedNodeClassName = classnames(
    'dbi-add-entities-highlight',
    className,
  );

  if (!isSeparate) {
    try {
      // 关键字高亮匹配不拆分的场景
      // eslint-disable-next-line arrow-body-style
      return replaceMatchedKeywordWithReactElement(
        isIgnoreCase,
        text,
        keyword,
        payload => {
          return React.createElement(
            'span',
            {
              // eslint-disable-next-line no-plusplus
              key: `dbi-highlight-node-${HIGHLIGHT_NODE_UNIQUE_COUNT++}`,
              className: matchedNodeClassName,
            },
            React.createElement('b', {}, [payload]),
          );
        },
        isIgnoreCase,
      );
    } catch (exception) {
      return text;
    }
  }

  return text.split('').map((char, index) => {
    const charTemp = isIgnoreCase ? char.toLowerCase() : char;
    const isHighlight = keywordTemp.includes(charTemp);

    if (!isHighlight) return char;

    return React.createElement(
      'span',
      {
        key: index,
        className: matchedNodeClassName,
      },
      React.createElement('b', {}, [char]),
    );
  });
};

/**
 * 基于当前的查询参数，进行新增、修改、删除等操作，返回组合修改后的查询参数字符串
 * @param query 要新增、或修改的查询参数
 * @param deleteQuery 要删除的查询参数key集合
 * @returns 更新后的查询参数字符串
 */
export const getComposedURLSearchParamsStr = (
  query: { [key: string]: any },
  deleteQuery?: string[],
): string => {
  const originalSearchParamsStr = location?.search?.substring(1) || '';
  const searchParams = new URLSearchParams(originalSearchParamsStr);

  // 新增、或者修改
  if (query && Object.prototype.toString.call(query) === '[object Object]') {
    Object.entries(query).forEach(([key, value]) => {
      if (key) {
        searchParams.set(key, value);
      }
    });
  }

  // 删除
  if (Array.isArray(deleteQuery) && deleteQuery.length) {
    deleteQuery.forEach(key => {
      if (searchParams.has(key)) {
        searchParams.delete(key);
      }
    });
  }

  return searchParams.toString();
};

/**
 * 获取指定 URLSearchParams
 */
export const getURLSearchParams = (paramName: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName);
};

/**
 * 更新当前url的查询参数，不会触发页面刷新、组件state的重置，纯替换url链接上的查询参数
 * @param query 需要更新的查询参数，但是目前只支持替换、新增，不支持删除，后续有要删除查询参数的 可以在这个方法上扩展
 * @param deleteQuery 要删除的查询参数，key名
 */
export const updateURLSearchParams = (
  query: { [key: string]: any },
  deleteQuery?: string[],
) => {
  const href = location?.href || '';
  const index = href?.indexOf('?') || -1;
  const url = index === -1 ? href : href?.substring(0, index);
  const searchParamsStr = getComposedURLSearchParamsStr(query, deleteQuery);
  const newUrl = searchParamsStr ? `${url}?${searchParamsStr}` : url;
  history.replaceState(null, '', newUrl);
};

export const getMixedBarType = (data: any[], calacuKey: string) => {
  let hasPositive = false;
  let hasNegative = false;

  if (!Array.isArray(data)) {
    return mixedBarType.positive;
  }

  for (const item of data) {
    if (hasPositive && hasNegative) {
      break;
    }
    if (item?.[calacuKey] > 0) {
      hasPositive = true;
    } else if (item?.[calacuKey] < 0) {
      hasNegative = true;
    }
  }

  if (hasPositive && !hasNegative) {
    return mixedBarType.positive;
  }
  if (!hasPositive && hasNegative) {
    return mixedBarType.negative;
  }
  if (hasPositive && hasNegative) {
    return mixedBarType.mixed;
  }
  return mixedBarType.positive;
};

export const getPositiveOrMixedType = (params: {
  data: any[];
  typeKey: string;
  calacuKey: string;
}) => {
  const { data, typeKey, calacuKey } = params;

  if (!Array.isArray(data)) {
    return mixedBarType.positive;
  }

  for (const typeMsg of data) {
    if (!Array.isArray(typeMsg?.[typeKey])) {
      continue;
    }
    const type = getMixedBarType(typeMsg[typeKey], calacuKey);
    if (type === mixedBarType.mixed) {
      return type;
    }
  }

  return getMixedBarType(
    data.flatMap(item => item[typeKey]),
    calacuKey,
  );
};

// 整理计算列表的最大值
export const calculateConstants = (params: {
  data: any[];
  typeKey: string;
  calacuKey1: string;
  calacuKey2: string;
  cacalMax: number;
}) => {
  const { data, typeKey, calacuKey1, calacuKey2, cacalMax } = params;
  let maxVal1 = 0;
  let maxVal2Abs = 0;
  if (!Array.isArray(data)) {
    return {};
  }
  data?.forEach(item => {
    let calaList = data;
    if (typeKey) {
      calaList = item?.[typeKey];
    }
    if (!Array.isArray(calaList)) {
      return {};
    }
    for (
      let i = 0;
      i < (Number(cacalMax) > 0 ? cacalMax : calaList?.length);
      i++
    ) {
      const typeMsg = calaList?.[i];
      const value1 = isNaN(parseFloat(typeMsg?.[calacuKey1]))
        ? 0
        : parseFloat(typeMsg?.[calacuKey1]);
      const value2 = isNaN(parseFloat(typeMsg?.[calacuKey2]))
        ? 0
        : parseFloat(typeMsg?.[calacuKey2]);
      maxVal1 = Math.max(value1, maxVal1);
      maxVal2Abs = Math.max(Math.abs(value2), maxVal2Abs);
    }
  });
  return {
    maxVal1,
    maxVal2Abs,
  };
};

export const getBarWidth = (
  value: string | number,
  maxValue: string | number,
) => {
  const parsedValue = parseFloat(value as string);
  const parsedMaxValue = parseFloat(maxValue as string);
  let percentWidth = Math.abs(parsedValue / parsedMaxValue) * 100;

  if (isNaN(percentWidth)) {
    percentWidth = 0;
  } else if (percentWidth < 1 && percentWidth > 0) {
    percentWidth = 0.5;
  }

  return `${percentWidth}%`;
};

/**
 * 下载文件
 * @param cfg.url 文件连接
 * @param cfg.fileName 文件名称
 */
export const downloadFile = (cfg: { url: string; fileName: string }) => {
  const url = cfg?.url || '';
  const fileName = cfg?.fileName || '';
  const iframe = document.createElement('iframe');
  const linker = document.createElement('a');
  iframe.style.display = 'none';
  linker.setAttribute('href', url);
  linker.setAttribute('download', fileName);
  document.body.appendChild(iframe);
  linker.click();
  document.body.removeChild(iframe);
};

/*
 * 给列表数据中每条数据 追加 指定的字段
 * @param list 列表数据
 * @param fields 追加字段对象
 * @returns 追加字段后的列表数据
 */
export const appendFields2ListData = <T1, T2>(
  list: T1[],
  fields: T2,
): T1[] | (T1 & T2)[] => {
  if (!Array.isArray(list)) return [];
  if (!isObject(fields)) return list;
  return list.map(item => {
    if (!isObject(item)) return item;
    return { ...item, ...fields };
  });
};

export const appendDynamicFields2ListData = <T1, T2>(
  list: T1[],
  getFields: (payload: T1) => T2,
): T1[] | (T1 & T2)[] => {
  if (!Array.isArray(list)) return [];
  if (typeof getFields !== 'function') return list;
  return list.map(item => {
    if (!isObject(item)) return item;
    const fields = getFields(item) || {};
    return { ...item, ...fields };
  });
};

export const mapOptionValue2Text = (
  value: string,
  options: SelectOption[],
): string => {
  if (!value) return '';
  if (!Array.isArray(options)) return value;
  const option = options.find(item => item?.value === value);
  return (option?.text as string) || value;
};

export const openTab = (path: string) => {
  const basePath = getBasePath();
  const pathTmp = path?.startsWith('/') ? path?.substring(1) : path;
  if (isSafari()) {
    const page: any = window.open('', '_blank');
    page.location = `${location.origin}${basePath}/intelligence/${pathTmp}`;
  } else {
    window.open(`${basePath}/intelligence/${pathTmp}`, '_blank');
  }
};

const getOptionTextDefault = (option: ISelectOptionWithGroup): string =>
  (option?.text as string) || '';
// tea Select组件 自定义filter逻辑方法
export const buildSelectFilterAccepter = (props: {
  // 是否忽略大小写，默认true
  ignoreCase?: boolean;
  // 是否把输入关键字 拆分为单个字符 匹配，默认true
  separate?: boolean;
  // 获取option的text的实现，必须返回string类型的，用于匹配字符串
  getOptionText?: (option: ISelectOptionWithGroup) => string;
}) => {
  const {
    ignoreCase = true,
    separate = true,
    getOptionText = getOptionTextDefault,
  } = props || {};

  /**
   * 处理是否拆分字符，统一返回string[]格式
   * 拆分：返回char[]
   * 不拆分：返回string[]
   * @param inputValue 关键字
   * @returns 字符串数组（拆分时，是字符数组）
   */
  const getKeywords = (inputValue: string): string[] => {
    const inputValueTrim = inputValue?.trim();
    if (!inputValueTrim) return [];
    return separate ? inputValueTrim?.split('') : [inputValueTrim];
  };

  return (inputValue: string, option: ISelectOptionWithGroup): boolean => {
    const rawOptionText = getOptionText?.(option) || '';
    // Ensure optionText is always a string (it might be a React element after highlighting)
    let optionText =
      typeof rawOptionText === 'string'
        ? rawOptionText
        : String(option?.value || '');
    let keywords = getKeywords(inputValue);
    if (!optionText) return false;
    if (!keywords?.length) return true;
    // 忽略大小写时，把所有字符传处理为小写
    if (ignoreCase) {
      optionText = optionText?.toLowerCase();
      keywords = keywords?.map(keyword => keyword?.toLowerCase());
    }
    // 过滤掉option的text 包含 keywords中所有字符串的option
    return keywords.every(keyword => {
      return optionText?.includes(keyword);
    });
  };
};

// 数值排序方法
export const numberComparator = (
  a: number,
  b: number,
  isAsc: boolean,
): number => {
  if (Number.isNaN(a) && Number.isNaN(b)) return 0;
  if (Number.isNaN(a)) return isAsc ? -1 : 1;
  if (Number.isNaN(b)) return isAsc ? 1 : -1;
  return isAsc ? a - b : b - a;
};

const NOTIFICATION_COMMON_CFGS = {
  unique: true,
  duration: 3000,
};
export const notificationSimple = (
  type: NotificationProps['type'],
  description: string,
) => {
  const method = notification[type] || 'warn';
  method?.({
    ...NOTIFICATION_COMMON_CFGS,
    description: description || '',
  });
};

// 获取下载接口返回流中的文件名称 headers: 响应头信息
export const analysisFileNameFromResHeaders = (
  headers: AxiosResponseHeaders,
): string => {
  const contentDisposition = headers?.['content-disposition'] || '';
  let fileName = contentDisposition?.split(';')[1] || '';
  fileName = fileName?.split('=')[1] || '';
  fileName = fileName?.trim()?.replace(/['"]/g, '') || '';
  try {
    fileName = decodeURIComponent(fileName);
  } catch (error: any) {
    fileName = fileName;
  }
  return fileName || '';
};

/**
 * 预览Markdown文本，返回innerHTML内容
 * @param text Markdown文本内容
 * @returns 可供预览的innerHTML内容
 */
export const previewMarkdown = (text: string): string => {
  if (!text) return '';
  const content = new markdownIt({
    // 在源码中启用 HTML 标签
    html: true,
    // 使用 '/' 来闭合单标签 （比如 <br />）
    xhtmlOut: true,
    // 转换段落里的 '\n' 到 <br>
    breaks: true,
    // 将类似 URL 的文本自动转换为链接
    linkify: true,
    typographer: true,
  });

  return DOMPurify.sanitize(content.render(text), {
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['s'],
  });
};

/**
 * 过滤Markdown标签文本，返回普通文本
 * @param text Markdown文本
 * @returns 普通文本
 */
export const filterMarkdown = (text: string): string => {
  if (!text) return '';
  return text.replace(/<[^>]+>/gi, '');
};

/**
 * 修改a标签的打开窗口
 * @param linkerText a标签字符串
 * @param target a标签打开窗口
 * @returns a标签字符串
 */
export const handleLinkerTarget = (
  linkerText: string,
  target: 'self' | '_blank' = '_blank',
): string => {
  if (!linkerText) return '';
  return linkerText.replace(/(<a\s+[^>]+)>/gi, `$1 target="${target}">`);
};

/**
 * 判断两个集合是否一样，无视集合中元素的索引是否一致的问题，只考虑集合长度是否一样，集合中所有的元素是否都在另一个集合中存在
 * 使用场景1、用来判断国家是否勾选改变过
 * @param list1 集合1
 * @param list2 集合2
 * @param isEqual 判断集合中元素是否相等的方法，默认采用 === 的方式判断是否相等
 * @returns 两个集合是否一样
 */
export const isListEqual = <T>(
  list1?: T[],
  list2?: T[],
  isEqual = (a: T, b: T) => a === b,
): boolean => {
  if (!Array.isArray(list1) || !Array.isArray(list2)) return false;
  if (list1.length !== list2.length) return false;
  return list1.every(item1 => {
    return list2.find(item2 => isEqual(item1, item2));
  });
};

// 返回搜索游戏权限结合的游戏排序权重值
export const getSearchSortNum = (opinionNum: number, dashboardNum: number) => {
  switch (dashboardNum) {
    case 2: {
      if (opinionNum === 2) {
        return 1;
      }
      if (opinionNum === 1) {
        return 2;
      }
      return 5;
    }
    case 1: {
      if (opinionNum === 2) {
        return 3;
      }
      if (opinionNum === 1) {
        return 4;
      }
      return 6;
    }
    case 0: {
      if (opinionNum === 2) {
        return 7;
      }
      if (opinionNum === 1) {
        return 8;
      }
    }
    default:
      return 9;
  }
};

// 获取统一ID的数据源提示信息是否要展开
export const getSourceState = () => {
  const sourcelistStateTemp =
    localStorage.getItem('dbi_pcconsole_sourcelist_state') || '';
  const sourcelistStateObj =
    sourcelistStateTemp !== '' ? JSON.parse(sourcelistStateTemp) : {};
  return sourcelistStateObj?.value ?? true;
};
// 前端计算比环比 通用方法
export const getRrowthRate = (
  currentValue: any,
  previousValue: any,
  isNeedPre = true,
) => {
  const current = Number(currentValue);
  const previous = Number(previousValue);
  if (previous === 0 || !previousValue) {
    return ''; // 避免除零
  }

  const growthRate = ((current - previous) / previous) * 100;
  if (isNeedPre) {
    return `${Number(growthRate.toFixed(2))}%`;
  }
  return Number(growthRate.toFixed(2)) * 0.01; // 保留两位小数
};

// 通用，获取下载表头和表体方法
export const buildDownloadData = (config: any, data: any[]) => {
  const result: any = [];
  const head: any = [];
  data.forEach((e, i) => {
    const row: any = [];
    result.push(row);
    config.forEach((c: any) => {
      let flag = false;
      if (c.when) {
        flag = c.when();
      } else {
        flag = true;
      }
      if (flag) {
        if (i == 0) {
          // 放入表头
          head.push(c.title);
        }
        // 放入表体
        if (c.key) {
          if (typeof c.key === 'string') {
            row.push(e[c.key]);
          } else if (typeof c.key === 'function') {
            row.push(c.key(e));
          }
        }
      }
    });
  });
  return [head, ...result];
};

// 通用，获取下载表头和表体方法
export const buildDownloadDataNew = (config: any, data: any[]) => {
  const result: any = [];
  const heads: any = [];
  const titles: string[] = [];

  config.forEach((c: any) => {
    let visible = false;
    if (typeof c.when === 'function') {
      visible = c.when();
    } else if (typeof c.when === 'boolean') {
      visible = c.when;
    } else {
      visible = true;
    }
    // 放入表头
    if (visible) {
      heads.push(c);
      titles.push(c.title);
    }
  });

  data?.forEach(e => {
    const row: any = [];
    result.push(row);

    heads.forEach((h: any) => {
      // 放入表体
      if (h.key) {
        if (typeof h.key === 'string') {
          row.push(e[h.key]);
        } else if (typeof h.key === 'function') {
          row.push(h.key(e));
        }
      }
    });
  });

  return [titles, ...result];
};


// 判断是否为可转数字的字符串，可转数字的字符串，如：'123'、'123.456'、'123,456'、'123,456.789'，能转为数字的字符串，不能就返回原字符串
export const changeNumberChartFun = (valStr: string) => {
  if (valStr === '新进榜') {
    return 'New Entry';
  }
  const num = Number(decimalFormat(valStr, 1, false, ''));
  if (Number.isNaN(num)) return valStr;
  if (num === 0) return '=';
  return num;
};

export const ifCanChangeNumberFun = (valStr: string) => {
  const num = Number.parseInt(valStr, 10);
  return !Number.isNaN(num);
};

export const getEnRankChange = (item: any) => {
  if (item.consecutive_days > 0) {
    return `Top for ${item.consecutive_days} Days`;
  }
  if (!ifCanChangeNumberFun(item.rank_change)) {
    if (item.rank_change === '新进榜') {
      return 'New Entry';
    }
    return item.rank_change;
  }
  return '';
};

// 通用，根据国家或地区名，返回中文
export const getCountryAndRegionByShotKey = (
  key: string,
  regionOptions: any,
) => {
  if (key === 'global') {
    return {
      region_chinese: '全球',
      region_english: 'global',
      country_chinese: '全球',
      country_english: 'global',
    };
  }
  if (key === 'global_wo_china') {
    return {
      region_chinese: '全球',
      region_english: 'Global except China mainland',
      country_chinese: '全球（不包括中国大陆）',
      country_english: 'Global except China mainland',
    };
  }
  for (const item of regionOptions) {
    const contry_set = item?.country_set || [];
    if (item?.area_code === key) {
      return {
        region_chinese: item?.area_name_ch || '',
        region_english: item?.area_name_en || '',
        country_chinese: item?.area_name_ch || '',
        country_english: item?.area_name_en || '',
      };
    }
    for (const t of contry_set) {
      if (key === t.country_code) {
        return {
          region_chinese: item?.area_name_ch || '',
          region_english: item?.area_name_en || '',
          country_chinese: t?.country_name_ch || '',
          country_english: t?.country_name_en || '',
        };
      }
    }
  }
};

export const getRegionByCode = (
  regionCode?: string,
  regions?: IRegion[] | null,
): IRegion | null => {
  if (!regionCode) return null;
  const lowerRegionCode = regionCode.toLowerCase();
  if (lowerRegionCode === 'global') {
    return {
      area_code: regionCode,
      area_name_en: 'Global',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (lowerRegionCode === 'global_wo_china') {
    return {
      area_code: regionCode,
      area_name_en: 'Global except China mainland',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;
  const region = regions.find((item) => lowerRegionCode === item?.area_code?.toLowerCase());
  return region || null;
};

export const getRegionByCountryCode = (
  countryCode?: string,
  regions?: IRegion[] | null,
): IRegion | null => {
  if (!countryCode) return null;
  const lowerCountryCode = countryCode.toLowerCase();
  if (lowerCountryCode === 'global') {
    return {
      area_code: 'global',
      area_name_en: 'Global',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (lowerCountryCode === 'global_wo_china') {
    return {
      area_code: 'global_wo_china',
      area_name_en: 'Global except China mainland',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;

  for (const region of regions) {
    const countries = region?.country_set || [];
    for (const country of countries) {
      if (lowerCountryCode === country?.country_code?.toLowerCase()) {
        return region;
      }
    }
  }

  return null;
};

export const getCountryByCode = (
  countryCode?: string,
  regions?: IRegion[] | null,
): ICountry | null => {
  if (!countryCode) return null;
  const lowerCountryCode = countryCode.toLowerCase();
  if (lowerCountryCode === 'global') {
    return {
      country_code: countryCode,
      country_name_en: 'Global',
      country_name_ch: '全球',
    };
  }
  if (lowerCountryCode === 'global_wo_china') {
    return {
      country_code: countryCode,
      country_name_en: 'Global except China mainland',
      country_name_ch: '全球（不包括中国大陆）',
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;

  for (const region of regions) {
    const countries = region?.country_set || [];
    for (const country of countries) {
      if (lowerCountryCode === country?.country_code?.toLowerCase()) {
        return country;
      }
    }
  }

  return null;
};

/**
 * 组装情报路由完整path，统一加intelligence的路由前缀
 * @param subPath 路由path（不带intelligence前缀）
 * @return 完整的情报路由path
 */
export const buildDBIRoutePath = (subPath?: string): string => {
  if (!subPath) return '';
  if (subPath.startsWith('/intelligence')) return subPath;
  return subPath.startsWith('/')
    ? `/intelligence${subPath}`
    : `/intelligence/${subPath}`;
};

/**
 * 获取当前路由path，包含查询参数，hash，返回值不带/v2前缀，因为路由跳转时也不能带v2前缀
 */
export const getCurrentRoutePath = () => {
  const { pathname, search, hash } = window.location;
  const basePath = getBasePath();
  return pathname.replace(new RegExp(`^${basePath}`), '') + search + hash;
};



/**
 * 构建简单的排序 comparator
 * @description 支持数字、字符串、日期字符串的排序，处理 null/undefined 值
 * @date 2025-10-18
 * @author lv.chen
 */
export const buildSimpleComparator = (orderInfo: { orderMetric: string; order: string }) => {
  const { orderMetric, order } = orderInfo;
  const isAsc = order === 'asc';

  if (orderMetric === 'metric_date') {
    const startDateComparator = getDateComparator(order as SortType);
    const endDateComparator = getDateComparator(SortType.Asc);

    return (a: any, b: any) => {
      const sortNum = startDateComparator(a?.metric_start_date, b?.metric_start_date);
      if (sortNum !== 0) {
        return sortNum;
      }

      return endDateComparator(a?.metric_end_date, b?.metric_end_date);
    };
  }

  return (a: any, b: any) => {
    const aValue = a?.[orderMetric];
    const bValue = b?.[orderMetric];

    // 处理 null/undefined
    if (isNullOrUndefined(aValue) && isNullOrUndefined(bValue)) return 0;
    if (isNullOrUndefined(aValue) === null) return 1;
    if (isNullOrUndefined(bValue) === null) return -1;

    // 数字比较
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return isAsc ? aValue - bValue : bValue - aValue;
    }

    // 字符串比较（包括日期字符串）
    const aStr = String(aValue);
    const bStr = String(bValue);
    const compareResult = aStr.localeCompare(bStr);
    return isAsc ? compareResult : -compareResult;
  };
};


/**
 * 表格排序逻辑Hook
 */
// export default function useTableSort(params: IUseTableSortParams): IUseTableSortReturn {
//   const { feedbackParams, fetchTrueDataFeedbackHistory, setPageIndex } = params;

//   const [sortConfig, setSortConfig] = useState<ISortConfig>({
//     sortKey: feedbackParams?.order_metric || 'metric_date',
//     sortVal: feedbackParams?.order || SortType.Desc,
//   });

//   /**
//    * 处理排序变更
//    */
//   const handleSortChange = (event: ISortChangeEvent): void => {
//     setSortConfig({
//       sortKey: event?.key,
//       sortVal: event?.sortVal,
//     });

//     // 排序时重置到第一页
//     setPageIndex(1);

//     const requestParams: IPublicFeedbackParams = {
//       ...feedbackParams,
//       order_metric: event?.key,
//       order: event?.sortVal,
//       page: 1,
//     };

//     fetchTrueDataFeedbackHistory(requestParams);
//   };

//   return {
//     sortConfig,
//     handleSortChange,
//   };
// }
